import React, { useState, useEffect } from 'react';
import { Shield, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CONSENT_KEY = 'manteo_cookie_consent';

const CookieBanner = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) {
      // Delay showing the banner for a better UX
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'true');
    setShow(false);
    // Reload or trigger event to initialize PostHog
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'false');
    setShow(false);
    window.dispatchEvent(new Event('cookieConsentChanged'));
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          className="fixed bottom-6 left-6 right-6 z-[100] md:left-auto md:right-6 md:max-w-md"
        >
          <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white/80 p-5 shadow-2xl backdrop-blur-xl dark:border-gray-800 dark:bg-gray-950/80">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
                <Shield size={20} />
              </div>
              <div className="flex-1 space-y-2">
                <h3 className="text-sm font-black uppercase tracking-tight text-gray-900 dark:text-white">
                  Help us build the future of Earth Intelligence
                </h3>
                <p className="text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                  We use <span className="font-bold text-gray-900 dark:text-white">PostHog</span> to understand how you use Manteo.
                  We <span className="font-bold text-emerald-600 dark:text-emerald-400 underline decoration-emerald-500/30 underline-offset-2">never sell or share your information</span>—it is only used to improve the product.
                </p>
                <div className="rounded-xl bg-amber-50/50 p-2 border border-amber-100/50 dark:bg-amber-900/10 dark:border-amber-800/30">
                  <p className="text-[10px] font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                    <Shield className="h-3 w-3" />
                    Tip: Disabling Ad-Blockers for manteo.ai helps us track bugs & performance better!
                  </p>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={handleAccept}
                    className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-white shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-500 hover:shadow-emerald-500/40"
                  >
                    <Check size={14} />
                    Accept Tracking
                  </button>
                  <button
                    onClick={handleDecline}
                    className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-gray-600 transition-all hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                  >
                    <X size={14} />
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
